import { useEffect, useState } from 'react'
import { MapPin, X, ExternalLink } from 'lucide-react'
import { Button } from './Button'

/**
 * "Come arrivare" button that opens a MODAL to pick Google Maps or Apple Mappe (instead of the old
 * dropdown). Both are universal links to the destination coordinates, so they open the native map
 * app when installed (and fall back to the web map otherwise).
 */
export function DirectionsButton({ lat, lon, label }: { lat: number; lon: number; label: string }) {
  const [open, setOpen] = useState(false)

  // Escape to close + lock background scroll while the modal is open (matches TicketModal).
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [open])

  const options = [
    { label: 'Google Maps', href: `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}` },
    { label: 'Apple Mappe', href: `https://maps.apple.com/?daddr=${lat},${lon}` },
  ]

  return (
    <div className="flex-none">
      <Button variant="outline" leftIcon={<MapPin className="h-4 w-4" />} onClick={() => setOpen(true)}>
        {label}
      </Button>

      {open && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label={label}
        >
          <div className="absolute inset-0 bg-ink-950/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-xs animate-fade-up overflow-hidden rounded-2xl bg-white shadow-pop ring-1 ring-ink-100">
            <header className="flex items-center justify-between border-b border-ink-100 px-4 py-3">
              <h3 className="flex items-center gap-2 text-sm font-bold text-ink-900">
                <MapPin className="h-4 w-4 text-brand-600" /> {label}
              </h3>
              <button
                onClick={() => setOpen(false)}
                aria-label="Chiudi"
                className="rounded-lg p-1.5 text-ink-400 transition hover:bg-ink-50 hover:text-ink-700"
              >
                <X className="h-4 w-4" />
              </button>
            </header>
            <div className="p-2">
              {options.map((o) => (
                <a
                  key={o.label}
                  href={o.href}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-between gap-3 rounded-xl px-4 py-3 text-sm font-semibold text-ink-700 transition-colors hover:bg-ink-50"
                >
                  {o.label}
                  <ExternalLink className="h-4 w-4 flex-none text-ink-400" />
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
